import { isNullOrUndefined } from 'common';

interface LocalStorage {
  set: <T>(key: string, value: T) => void;
  get: <T>(key: string) => T | null;
  remove: (key: string) => void;
  clear: () => void;
}

const localStorageCache: Record<string, unknown> = {};

const isLocalStorageAvailable =
  typeof window !== 'undefined' && !!window.localStorage;

export const localStorage: LocalStorage = {
  set: <T>(key: string, value: T): void => {
    if (isNullOrUndefined(value) && isLocalStorageAvailable) {
      return localStorage.remove(key);
    }
    localStorageCache[key] = value;
    if (isLocalStorageAvailable) {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  },

  get: <T>(key: string): T | null => {
    if (!isNullOrUndefined(localStorageCache[key]) && isLocalStorageAvailable) {
      return localStorageCache[key] as T;
    }
    if (!isLocalStorageAvailable) {
      return null;
    }
    const localStorageValue = window.localStorage.getItem(key);
    try {
      localStorageCache[key] = JSON.parse(localStorageValue ?? 'null');
    } catch (e) {
      localStorageCache[key] = localStorageValue;
    }
    return localStorageCache[key] as T;
  },

  remove: (key: string): void => {
    delete localStorageCache[key];
    if (isLocalStorageAvailable) {
      window.localStorage.removeItem(key);
    }
  },

  clear: (): void => {
    if (isLocalStorageAvailable) {
      window.localStorage.clear();
      for (const key in localStorageCache) {
        delete localStorageCache[key];
      }
    }
  },
};
